import { Instagram, Youtube, Facebook, Linkedin, Link as LinkIcon } from "lucide-react";

export type AthleteLinks = {
  instagram_url?: string | null;
  tiktok_url?: string | null;
  facebook_url?: string | null;
  youtube_url?: string | null;
  linkedin_url?: string | null;
  custom_links?: { label: string; url: string }[] | null;
};

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
    <path d="M16.5 3a5.5 5.5 0 0 0 5 4.5v3.1a8.6 8.6 0 0 1-5-1.6v6.7a6.3 6.3 0 1 1-6.3-6.3c.3 0 .6 0 .9.1v3.2a3.2 3.2 0 1 0 2.2 3V3h3.2z" />
  </svg>
);

type Variant = "compact" | "full";

const AthleteSocialLinks = ({
  links,
  variant = "compact",
  className = "",
  onClickStop = false,
}: {
  links: AthleteLinks;
  variant?: Variant;
  className?: string;
  /** Stop click propagation — useful when rendered inside a card that is itself a link. */
  onClickStop?: boolean;
}) => {
  const socials: { url: string | null | undefined; label: string; Icon: any }[] = [
    { url: links.instagram_url, label: "Instagram", Icon: Instagram },
    { url: links.tiktok_url, label: "TikTok", Icon: TikTokIcon },
    { url: links.facebook_url, label: "Facebook", Icon: Facebook },
    { url: links.youtube_url, label: "YouTube", Icon: Youtube },
    { url: links.linkedin_url, label: "LinkedIn", Icon: Linkedin },
  ];
  const customs = (links.custom_links ?? []).filter((l) => l && l.url && l.label);
  const visibleSocials = socials.filter((s) => !!s.url);

  if (visibleSocials.length === 0 && customs.length === 0) return null;

  const stop = onClickStop ? (e: React.MouseEvent) => e.stopPropagation() : undefined;

  if (variant === "compact") {
    // Icons only — fits on cards
    return (
      <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
        {visibleSocials.map(({ url, label, Icon }) => (
          <a
            key={label}
            href={url!}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stop}
            aria-label={label}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-muted hover:bg-secondary hover:text-secondary-foreground text-foreground transition-colors"
          >
            <Icon className="w-3.5 h-3.5" />
          </a>
        ))}
        {customs.map((l, i) => (
          <a
            key={`custom-${i}`}
            href={l.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stop}
            aria-label={l.label}
            title={l.label}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-muted hover:bg-secondary hover:text-secondary-foreground text-foreground transition-colors"
          >
            <LinkIcon className="w-3.5 h-3.5" />
          </a>
        ))}
      </div>
    );
  }

  // Full — pill buttons with labels
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {visibleSocials.map(({ url, label, Icon }) => (
        <a
          key={label}
          href={url!}
          target="_blank"
          rel="noopener noreferrer"
          onClick={stop}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-sm font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors"
        >
          <Icon className="w-4 h-4" />
          {label}
        </a>
      ))}
      {customs.map((l, i) => (
        <a
          key={`custom-${i}`}
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={stop}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-sm font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors"
        >
          <LinkIcon className="w-4 h-4" />
          {l.label}
        </a>
      ))}
    </div>
  );
};

export default AthleteSocialLinks;
